"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isKnownSignerType = exports.SIGNER_ROLES = void 0;
exports.consentRolesFromData = consentRolesFromData;
exports.hasUnreadableStoredRoles = hasUnreadableStoredRoles;
exports.readableConsentRolesFromData = readableConsentRolesFromData;
exports.isNonSigningSignerType = isNonSigningSignerType;
exports.getSignerRoleLabel = getSignerRoleLabel;
exports.consentRoleLabel = consentRoleLabel;
exports.findRoleByLabel = findRoleByLabel;
exports.signingOrders = signingOrders;
exports.signingOrderFromRoles = signingOrderFromRoles;
/**
 * Canonical base signer types shared by Studio UI and the consent bundler.
 * `value` is stored as `signerTypeId` / Cortex `ec__signer_type`; `label` is the
 * friendly default display name when the role string matches a base type.
 */
exports.SIGNER_ROLES = [
    { value: 'participant', label: 'Participant' },
    { value: 'non-signing participant', label: 'Non-signing participant' },
    { value: 'third party', label: 'Third party' },
    { value: 'internal signer', label: 'Internal signer' },
];
const isKnownSignerType = (value) => exports.SIGNER_ROLES.some((role) => role.value === value);
exports.isKnownSignerType = isKnownSignerType;
/**
 * The document roles a CONSENT_TEMPLATE's Library `data` holds. A non-empty `data.roles` wins; otherwise
 * the roles are rebuilt from the MOM projection `ec__signer_roles` (in `ec__order`), which is all a
 * legacy template carries. Studio's editor and the consent bundler both read roles through this, so the
 * bundler never refuses, compiles or writes back a role list other than the one the Roles tab shows.
 *
 * A MOM signer type outside {@link SIGNER_ROLES} (a legacy `site`) is kept as it is, never mapped to
 * `participant`: that would hand a site signer the participant's signature. The publish rules refuse it
 * as `signerTypeUnknown` until the author picks a signer type in the Roles tab.
 */
function consentRolesFromData(data) {
    const stored = data.roles;
    if (Array.isArray(stored) && stored.length > 0) {
        return stored;
    }
    const momRoles = data.ec__signer_roles;
    if (!Array.isArray(momRoles)) {
        return [];
    }
    return [...momRoles]
        .sort((left, right) => { var _a, _b; return Number((_a = left.ec__order) !== null && _a !== void 0 ? _a : 0) - Number((_b = right.ec__order) !== null && _b !== void 0 ? _b : 0); })
        .map((role) => ({
        id: typeof role.ec__key === 'string' ? role.ec__key : '',
        role: typeof role.ec__role === 'string' ? role.ec__role : '',
        signerTypeId: (typeof role.ec__signer_type === 'string' ? role.ec__signer_type : ''),
        roleRequired: Boolean(role.ec__required),
    }));
}
const isPlainObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isAbsentOrString = (value) => value === undefined || value === null || typeof value === 'string';
// The role label, the publish rules and the MOM projection all trim these three fields, so any other
// type throws there. `roleRequired` is only ever read through Boolean().
const isReadableStoredRole = (role) => isPlainObject(role) &&
    isAbsentOrString(role.id) &&
    isAbsentOrString(role.role) &&
    isAbsentOrString(role.signerTypeId);
/**
 * Whether the role list {@link consentRolesFromData} reads (a non-empty `data.roles`, else the
 * `ec__signer_roles` fallback) holds an entry Studio cannot read. A `data.roles` entry must be a plain
 * object whose `id`, `role` and `signerTypeId` are strings or absent; a fallback entry only has to be a
 * plain object, because the conversion drops any field that is not a string. Studio never writes such an
 * entry, and the readers of a role's fields throw on it, so the template's stored data is damaged: the
 * bundler refuses it as `templateDataInvalid`, and Studio must refuse it the same way rather than crash.
 */
function hasUnreadableStoredRoles(data) {
    const stored = data.roles;
    if (Array.isArray(stored) && stored.length > 0) {
        return stored.some((role) => !isReadableStoredRole(role));
    }
    const momRoles = data.ec__signer_roles;
    return Array.isArray(momRoles) && momRoles.some((role) => !isPlainObject(role));
}
/**
 * {@link consentRolesFromData} with the unreadable entries dropped, for display only. Reads the same list
 * even when dropping them leaves it empty, so the editor never swaps in the other list. Never persist the
 * result: writing it back would erase the damage the bundler refuses on.
 */
function readableConsentRolesFromData(data) {
    const stored = data.roles;
    if (Array.isArray(stored) && stored.length > 0) {
        return stored.filter(isReadableStoredRole);
    }
    const momRoles = data.ec__signer_roles;
    return consentRolesFromData({
        ec__signer_roles: Array.isArray(momRoles) ? momRoles.filter(isPlainObject) : momRoles,
    });
}
/**
 * A non-signing participant is associated with the document but never signs it: Cortex creates its
 * invite already complete, so it holds no signing order, carries no required flag, and cannot own a
 * signature, initials or data field (SSP-4198).
 */
function isNonSigningSignerType(signerType) {
    return signerType === 'non-signing participant';
}
/** Friendly label for a base signer type value; unknown roles pass through unchanged. */
function getSignerRoleLabel(role) {
    var _a;
    if (!role) {
        return exports.SIGNER_ROLES[0].label;
    }
    const row = exports.SIGNER_ROLES.find((r) => r.value === role);
    return (_a = row === null || row === void 0 ? void 0 : row.label) !== null && _a !== void 0 ? _a : role;
}
/**
 * Identity used on signature/data fields and in Cortex `ec__role`.
 * Authors can leave the Role name blank; the signer type label is then the pickable name.
 */
function consentRoleLabel(role) {
    var _a;
    const named = ((_a = role.role) !== null && _a !== void 0 ? _a : '').trim();
    return named || getSignerRoleLabel(role.signerTypeId);
}
/**
 * Resolves a field's `ec__signer_role` back to the document role it names.
 *
 * Fields store the role's {@link consentRoleLabel}, not the raw Role name, so an unnamed role is
 * still addressable by its signer type label. Matching on `role.role` here instead would miss every
 * blank-named role and silently hand the caller its default (`participant`) signer type.
 */
function findRoleByLabel(signerRole, roles) {
    if (!signerRole) {
        return undefined;
    }
    return roles.find((role) => consentRoleLabel(role) === signerRole);
}
/**
 * 1-based signing order of each role, by its position in the Roles tab list; 0 where the role does
 * not sign. Non-signing participants take up no position rather than being skipped over, so the
 * roles that do sign stay numbered 1..n with no gap wherever one sits in the list (SSP-4198).
 *
 * The single source for the order badge the author sees, the `ec__order` sent to Cortex, and the
 * order compiled onto every signature field.
 */
function signingOrders(roles) {
    let position = 0;
    return roles.map((role) => (isNonSigningSignerType(role.signerTypeId) ? 0 : ++position));
}
/**
 * Signing order of the role a field names, matched on {@link consentRoleLabel} so a blank-named role
 * keeps its place in the order. A field on a non-signing role gets 0 — the same order that role's own
 * row carries — not undefined: undefined means "names no role in this list", which is the only case
 * where a caller may fall back to a leftover per-field value (a legacy projection with no role list).
 * Collapsing the two would let a leftover order revive on a field whose role never signs (SSP-4198).
 */
function signingOrderFromRoles(signerRole, roles) {
    if (!signerRole) {
        return undefined;
    }
    const index = roles.findIndex((role) => consentRoleLabel(role) === signerRole);
    return index >= 0 ? signingOrders(roles)[index] : undefined;
}
//# sourceMappingURL=signer_roles.js.map